// Organisations partenaires (spec § 3, 23/10/2026) : liste « À faire » des prochaines actions de tous les pipelines.
import type { Deal, DealStage, PartnerOrg } from "../types";
import { pipelineForDeal } from "./partnerships";

export type Pipeline = { dealStages: DealStage[]; dealClosedStages: string[] };

export type TodoItem = { deal: Deal; own: boolean; overdue: boolean };

/**
 * Prochaines actions des affaires en cours de tous les pipelines, la plus proche d'abord ; les affaires sans date
 * viennent ensuite, les plus récemment touchées d'abord. Une affaire à une étape de fin de son pipeline n'y est pas.
 */
export const todoItems = (
  deals: Deal[],
  myOrg: PartnerOrg | undefined | null,
  orgs: PartnerOrg[],
  client: Pipeline,
  today: string,
): TodoItem[] =>
  deals
    .filter(
      (deal) =>
        !deal.archived_at &&
        !pipelineForDeal(deal, myOrg, orgs, client).dealClosedStages.includes(
          deal.stage,
        ) &&
        (deal.next_action || deal.next_action_date),
    )
    .map((deal) => ({
      deal,
      own: deal.org_id != null && deal.org_id === myOrg?.id,
      overdue: !!deal.next_action_date && deal.next_action_date < today,
    }))
    .sort((a, b) => {
      const da = a.deal.next_action_date;
      const db = b.deal.next_action_date;
      if (da && db) return da.localeCompare(db);
      if (da) return -1;
      if (db) return 1;
      return (b.deal.updated_at ?? "").localeCompare(a.deal.updated_at ?? "");
    });

/** Étapes saisies dans l'écran de l'organisation : code tiré du nom quand il manque, codes uniques. */
export const normalizeStages = (
  stages: { value?: string; label: string }[],
) => {
  const used = new Set<string>();
  return stages
    .filter((stage) => stage.label.trim())
    .map((stage) => {
      const base =
        stage.value?.trim() ||
        stage.label
          .trim()
          .toLowerCase()
          .normalize("NFD")
          .replace(/[̀-ͯ]/g, "")
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-|-$/g, "") ||
        "etape";
      let value = base;
      for (let n = 2; used.has(value); n++) value = `${base}-${n}`;
      used.add(value);
      return { value, label: stage.label.trim() };
    });
};

/** Places d'une organisation (limite de la base, `org_member_limit`) : un membre suspendu ne compte pas. Tant que la
 * limite n'est pas lue, rien n'est bloqué ; la base refuse de toute façon un membre de trop. */
export const orgSeats = (
  members: { disabled?: boolean | null }[],
  limit: number | undefined,
) => {
  const active = members.filter((member) => !member.disabled).length;
  return { active, limit, full: limit != null && active >= limit };
};
